"use client";
import clsx from "clsx";
import Image from "next/image";
import type { ChangeEvent } from "react";
import { useId, useState } from "react";
import type { FieldValues, UseFormRegister } from "react-hook-form";

export const useReservationAgreeButton = ({
  label,
  initialChecked = false,
}: {
  label: string;
  initialChecked?: boolean;
}) => {
  const [checked, setChecked] = useState(initialChecked);
  return {
    checked,
    renderReservationAgreeButton: () => {
      return (
        <ReservationAgreeButton
          checked={checked}
          onToggle={setChecked}
          label={label}
        />
      );
    },
  };
};

const JA_MESSAGES = {
  required: "必須",
  optional: "任意",
};
export type ReservationAgreeButtonMessages = typeof JA_MESSAGES;

type ReservationAgreeButtonProps<T extends FieldValues> = {
  label: string;
  required?: boolean;
  checked?: boolean;
  onToggle?: (checked: boolean) => void;
  inputRHFArgs?: ReturnType<UseFormRegister<T>>;
  testId?: string;
};

export function ReservationAgreeButton<T extends FieldValues>({
  label,
  required = false,
  checked,
  onToggle,
  inputRHFArgs,
  testId,
  messages = JA_MESSAGES,
}: {
  messages?: ReservationAgreeButtonMessages;
} & ReservationAgreeButtonProps<T>) {
  const id = useId();

  return (
    <div
      className={clsx(
        "group flex w-full max-w-[315px] content-stretch items-center justify-center",
        `border-skogecha bg-swhite rounded-[30px] border`,
        "has-checked:border-sorange",
        "transition-colors hover:bg-[#efefef] hover:shadow-sm",
      )}
    >
      <input
        type="checkbox"
        id={id}
        className="hidden"
        {...(inputRHFArgs
          ? { ...inputRHFArgs }
          : {
              onChange: (e: ChangeEvent<HTMLInputElement>) =>
                onToggle?.(e.target.checked),
              checked: checked,
            })}
      />
      <label
        htmlFor={id}
        data-testid={testId}
        className="flex w-full items-center justify-center gap-[12px] px-[16px] py-[12px] hover:cursor-pointer"
      >
        <div className="flex size-6 shrink-0 items-center justify-center">
          <Image
            className="hidden group-has-checked:block"
            src="/i/check-box-filled.svg"
            alt="チェック済みのチェックボックス"
            width={24}
            height={24}
          />
          <Image
            className="block group-has-checked:hidden"
            src="/i/check-box-outline-blank.svg"
            alt="チェックボックス"
            width={24}
            height={24}
          />
        </div>
        <p
          className={`group-has-checked:text-sorange text-skogecha min-w-0 text-center text-[12px] leading-normal font-bold tracking-[0.24px] text-wrap whitespace-pre-wrap select-none`}
        >
          {label}
        </p>
        <p
          className={clsx(
            "shrink-0 text-center text-[12px] leading-normal font-bold tracking-[0.24px] select-none",
            required ? "text-sorange" : "text-skogecha-60",
          )}
        >
          ({required ? messages.required : messages.optional})
        </p>
      </label>
    </div>
  );
}
